
import { useRef, useState, useEffect } from "react";
import { Separator } from "@/components/ui/separator";

export default function Promise() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasObserved, setHasObserved] = useState(false);
  const [isLogoHovered, setIsLogoHovered] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver !== 'undefined' && !hasObserved) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            setHasObserved(true);
            observer.disconnect();
          }
        },
        {
          root: null,
          threshold: 0.2,
        }
      );

      if (sectionRef.current) {
        observer.observe(sectionRef.current);
      }

      return () => observer.disconnect();
    } else if (!hasObserved) {
      setIsVisible(true);
      setHasObserved(true);
    }
  }, [hasObserved]);

  const practices = [
    {
      title: "The Sacred Pause",
      description: "Master the monastic practice that transforms decision quality"
    },
    {
      title: "Ancient Rhythm",
      description: "Habituate the pattern that creates both productivity and peace"
    },
    {
      title: "Contemplative Clarity",
      description: "Learn the practice that clarifies priorities instantly"
    },
    {
      title: "Morning Ritual",
      description: "Adopt the routine that shapes your entire day's impact"
    },
    {
      title: "Evening Review",
      description: "Experience the reflection that turns challenges into wisdom"
    },
    {
      title: "Presence Technique",
      description: "Practice the method that transforms every interaction"
    },
    {
      title: "Legacy Building",
      description: "Build enduring legacy through relationship and meaning"
    }
  ];

  return (
    <section 
      ref={sectionRef} 
      className="py-8"
      aria-labelledby="monastery-method-approach"
    >
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div 
            className={`opacity-0 ${
              isVisible ? "animate-fade-in animate-delay-600" : ""
            }`}
          >
            <div className="max-w-2xl mx-auto px-4 text-center space-y-8">
              {/* Logo with hover animation - made smaller and with more spacing */}
              <div 
                className="flex justify-center mb-12"
                onMouseEnter={() => setIsLogoHovered(true)}
                onMouseLeave={() => setIsLogoHovered(false)}
              >
                <div className={`relative transition-all duration-500 ${isLogoHovered ? "transform scale-110" : ""}`}>
                  <img 
                    src="/lovable-uploads/899dc610-61ca-419a-85d2-36471b23fda6.png" 
                    alt="The Monastery Method Logo" 
                    className="h-32 md:h-36 w-auto mx-auto"
                  />
                  {isLogoHovered && (
                    <div className="absolute inset-0 bg-[#8B5CF6]/5 rounded-full mix-blend-overlay animate-pulse"></div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <h2 className="text-2xl md:text-3xl font-serif text-charcoal leading-relaxed">
                  We are talking about unlocking a level beyond success: <span className="font-normal italic">the rare integration of purpose, achievement, and inner peace</span>
                </h2>
                <Separator className="w-16 h-[1px] bg-charcoal/30 mx-auto" />
              </div>
              
              <p className="text-lg text-softgray">
                Together we work hands-on through simple, powerful daily practices tested across centuries:
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center mt-8">
            {practices.map((practice, index) => (
              <div
                key={index}
                className={`opacity-0 w-full sm:w-[calc(50%-1rem)] md:w-80 px-6 mb-8 ${
                  isVisible ? "animate-fade-in" : ""
                }`}
                style={{ animationDelay: `${(index + 7) * 100}ms` }}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="mb-3">
                    <div className="w-[1px] h-8 bg-charcoal/30 mx-auto"></div>
                  </div>
                  <div>
                    <h3 className="text-lg font-serif mb-2">{practice.title}</h3>
                    <p className="text-softgray">{practice.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
